import React, { useState, useCallback, useRef, useEffect } from 'react';
import styles from './Search.scss';
import classNames from 'classnames';
import { isEmpty, debounce, size, map } from 'lodash';
import SearchIcon from '../../../../assets/images/search-15x15.svg';
import CloseSmallIcon from '../../../../assets/images/close-small-15x15.svg';
import Tooltip from '../../../Tooltip';
import { __, getURLParam, setURLParam } from '../../../../core/utils';
import SpinnerIcon from '../../../../assets/images/spinner-16x16.svg';
import ArrowRightMiddleIcon from '../../../../assets/images/arrow-right-middle-15x15.svg';
import ArrowLeftMiddleIcon from '../../../../assets/images/arrow-left-middle-15x15.svg';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { fabric } from 'fabric';
import PrepareSearch from '../../../../assets/images/prepare-search-16x16.svg';
import { centerOnResult } from '../../helpers/osdHelpers';

function SearchInline({
  onSearch,
  canvas_editor: { viewer, fabricOverlay },
  disabled,
  onPrepareSearch,
  ...props
}) {
  const search = useRef();
  const [search_active, set_search_active] = useState(!!getURLParam('query'));
  const [search_focus, set_search_focus] = useState(!!getURLParam('query'));
  const [query, set_query] = useState(getURLParam('query'));
  const [loading, set_loading] = useState(false);
  const [results, set_results] = useState([]);
  const [currentIndex, set_currentIndex] = useState(0);

  let canvas = fabricOverlay?.fabricCanvas();

  const fetchResults = (query) => {
    if (isEmpty(query)) {
      set_results([]);
      set_loading(false);
      return;
    }

    set_loading(true);

    onSearch(query).then(({ response }) => {
      const data = response.data;

      set_results(data || []);
      set_currentIndex(0);
      set_loading(false);

      //highlightWordsOnCanvas(data, 0);
    });
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      set_query(query);
      setURLParam('query', query);
    }, 600),
    []
  );

  useEffect(() => {
    if (size(results) > 0) {
      highlightWordsOnCanvas(results, currentIndex);
      viewer.viewport.goHome();

      centerOnResult(viewer, results[currentIndex]);
    } else {
      highlightWordsOnCanvas([], 0);
    }
  }, [results, currentIndex]);

  const _handleQuery = (e) => {
    const query = e.target.value;

    if (query.length > 1) {
      debouncedSearch(query);
    } else {
      //highlightWordsOnCanvas([], 0);
      set_results([]);
      set_currentIndex(0);

      debouncedSearch.cancel();
    }
  };

  const _setSearchFocus = (focus) => {
    set_search_focus(focus);
  };

  const _toggleSearch = () => {
    search.current.focus();

    set_search_active(!search_active);
  };

  const _clearSearch = () => {
    set_search_active(true);
    set_search_focus(true);
    set_query('');
    set_results([]);

    // highlightWordsOnCanvas([], 0);

    search.current.value = '';
    search.current.select();

    viewer.viewport.goHome();
  };

  const nextResult = () => {
    if (size(results) > 0) {
      const next = (currentIndex + 1) % size(results);
      set_currentIndex(next);
      centerOnResult(viewer, results[next]);

      //highlightWordsOnCanvas(results, next);
    }
  };

  const prevResult = () => {
    if (size(results) > 0) {
      const prev = (currentIndex - 1 + size(results)) % size(results);
      set_currentIndex(prev);
      centerOnResult(viewer, results[prev]);

      // highlightWordsOnCanvas(results, prev);
    }
  };

  const highlightWordsOnCanvas = (words, activeIndex) => {
    if (!canvas) return;

    canvas.getObjects().forEach((obj) => {
      if (obj.objectType === 'highlight') {
        canvas.remove(obj);
      }
    });

    if (!words.length) {
      canvas.renderAll();
      return;
    }

    map(words, (word, index) => {
      const isActive = index === activeIndex;

      const rect = new fabric.Rect({
        left: word.x,
        top: word.y,
        objectType: 'highlight',
        width: word.width,
        height: word.height,
        fill: isActive ? 'rgba(255,0,0,0.3)' : 'rgba(255,255,0,0.3)',
        stroke: isActive ? 'red' : 'yellow',
        strokeWidth: 1,
        selectable: false,
        evented: false,
      });

      canvas.add(rect);
      rect.setCoords();
    });

    canvas.requestRenderAll();
  };

  useEffect(() => {
    if (query) {
      fetchResults(query);
    }

    if (isEmpty(query)) {
      setURLParam('query', '');
    }
  }, [query]);

  const handlePrepareSearch = () => {
    onPrepareSearch().then(() => {
      search.current.focus();

      set_search_active(true);
    });
  };

  if (disabled) {
    return (
      <div className={styles.disabledSearch}>
        {props.loading && (
          <span className={styles.spinner}>
            <SpinnerIcon />
          </span>
        )}

        <Tooltip text={__('plans.tooltip.icon.working-set.parse-pdf-to-json')}>
          <PrepareSearch onClick={handlePrepareSearch} />
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        styles.search,
        search_focus && styles.focus,
        search_active && styles.active
      )}
    >
      <div className={styles.bar} />
      <input
        ref={search}
        type='text'
        defaultValue={query}
        placeholder={__('table.search.enter-search-criteria')}
        spellCheck='false'
        onChange={_handleQuery}
        onClick={() => search.current.select()}
        onBlur={() => _setSearchFocus(false)}
        onFocus={() => _setSearchFocus(true)}
      />

      {loading && (
        <span className={styles.spinner}>
          <SpinnerIcon />
        </span>
      )}

      {!loading && size(results) > 0 && (
        <div className={styles.navigation}>
          <span className={styles.counter}>
            {currentIndex + 1}/{results.length}
          </span>
          <div className={styles.arrowsBox}>
            <button className={styles.arrowButton} onClick={prevResult}>
              <ArrowLeftMiddleIcon />
            </button>
            <button className={styles.arrowButton} onClick={nextResult}>
              <ArrowRightMiddleIcon />
            </button>
          </div>
        </div>
      )}

      {!isEmpty(query) && (
        <CloseSmallIcon className={styles.close} onClick={_clearSearch} />
      )}
      {isEmpty(query) && (
        <Tooltip text={__('topbar.tooltip.search')}>
          <SearchIcon onClick={_toggleSearch} />
        </Tooltip>
      )}
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    canvas_editor: state.canvas_editor,
    table: state.table,
    working_set_plans: state.table['working_set_plans'],
    task_table: state.table['all_tasks'],
  };
};

export default withRouter(connect(mapStateToProps, null)(SearchInline));
